import { useEffect, useState, useCallback } from "react";
import { Plus, LogIn, Trophy, Globe, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatsGrid } from "../components/StatsGrid";
import { InstallPrompt } from "../components/InstallPrompt";
import { CreateTournamentDialog } from "../components/CreateTournamentDialog";
import { JoinTournamentDialog } from "../components/JoinTournamentDialog";
import { UpcomingMatches } from "../components/UpcomingMatches";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tournaments, setTournaments] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setError(false);
    api.get("/me/stats").then((r) => setStats(r.data)).catch(() => setError(true));
    api.get("/tournaments").then((r) => setTournaments(r.data)).catch(() => { setTournaments([]); setError(true); });
  }, []);
  useEffect(load, [load]);

  return (
    <div className="flex flex-col gap-6 sm:gap-12">
      {error && (
        <div className="card-surface border-red-500/40 p-5 flex items-center justify-between gap-4" data-testid="dashboard-error">
          <span className="text-red-300 text-sm">Non è stato possibile caricare i dati.</span>
          <Button onClick={load} variant="outline" className="rounded-full border-red-500/40 bg-transparent" data-testid="dashboard-retry-button">Riprova</Button>
        </div>
      )}
      <section className="flex flex-col md:flex-row md:items-end justify-between gap-4 sm:gap-6 fade-up">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-400">La tua dashboard</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight mt-1 sm:mt-2" data-testid="dashboard-greeting">
            Ciao, {user?.name?.split(" ")[0]}
          </h1>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setJoinOpen(true)} variant="outline" data-testid="open-join-tournament-button" className="flex-1 md:flex-none h-12 md:h-11 rounded-full border-[#26334D] bg-transparent hover:bg-[#182030]">
            <LogIn size={16} className="mr-2" /> Unisciti
          </Button>
          <Button onClick={() => setCreateOpen(true)} data-testid="open-create-tournament-button" className="flex-1 md:flex-none h-12 md:h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0D14] font-bold">
            <Plus size={16} className="mr-2" /> Crea torneo
          </Button>
        </div>
      </section>

      <div className="order-last md:order-none"><StatsGrid stats={stats} /></div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
        <section className="lg:col-span-7 space-y-3 sm:space-y-4">
          <InstallPrompt />
          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
            <Link to="/tornei" data-testid="dashboard-my-tournaments-link" className="card-surface p-5 sm:p-6 group hover:border-emerald-500/60 flex items-center justify-between gap-3">
              <div>
                <Trophy className="text-emerald-400" size={26} />
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight mt-3">I miei tornei</h2>
                <p className="text-sm text-slate-400">{tournaments ? `${tournaments.length} tornei` : "…"}</p>
              </div>
              <ChevronRight className="text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/tornei-pubblici" data-testid="dashboard-public-tournaments-link" className="card-surface p-5 sm:p-6 group hover:border-emerald-500/60 flex items-center justify-between gap-3">
              <div>
                <Globe className="text-emerald-400" size={26} />
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight mt-3">Tornei pubblici</h2>
                <p className="text-sm text-slate-400">Aperti a tutti, entra quando vuoi</p>
              </div>
              <ChevronRight className="text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </section>
        <section className="lg:col-span-5">
          <UpcomingMatches />
        </section>
      </div>

      <CreateTournamentDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
      <JoinTournamentDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
